import React from 'react'
import ReactDOM from 'react-dom'
import {Optional, Project} from 'data/types'
import {uniqueId} from 'lodash'
import published from 'utils/published'
import selector, {identitySelector} from 'utils/selector'
import ChromeExtensionService from './ChromeExtensionService'
import AddProblemOverlay from 'components/Chrome/AddProblemOverlay'
import ProblemInput from 'components/Chrome/ProblemInput'
import AuthService from 'services/AuthService'
import APIService from 'services/APIService'
import {StaticTokenProvider} from 'services/AuthTokenProvider'
import config from 'config'

/**
 * In this context, we are in the popup chrome content application
 */

type ContentServiceNode = {
  id: string
  pop: () => void
  node: any
}

export default class ChromeContentService extends ChromeExtensionService {
  public isMounted = published(false)
  private nodes: {[k: string]: ContentServiceNode} = {}
  private overlayNode = null as Optional<ContentServiceNode>
  private inputNode = null as Optional<ContentServiceNode>
  public project = published<Optional<Project>>(null)

  public get serviceName() {
    return 'contentApp'
  }

  public mountComponent = (component: any) => {
    const node = document.createElement('div')
    const nodeId = uniqueId('__itx-mounted-component_')
    document.body.appendChild(node)
    node.id = uniqueId()
    ReactDOM.render(component, node)

    const contentServiceNode: ContentServiceNode = {
      node,
      id: nodeId,
      pop: () => {
        try {
          node.parentElement?.removeChild(node)
        } catch (error) {
          // console.log('....error', error);
        }
      },
    }
    this.nodes[nodeId] = contentServiceNode
    return contentServiceNode
  }

  public mount = (input: any) => {
    AuthService.tokenProvider = new StaticTokenProvider(input.authToken)
    this.project.publish(input.project)
    document.addEventListener('click', this.onClick)
    this.overlayNode = this.mountComponent(<AddProblemOverlay />)
    this.requestor.exec('popupApp', 'close')
  }

  private onClick = (event: MouseEvent) => {
    const component = <ProblemInput service={this} remove={() => this.inputNode?.pop()} x={event.pageX} y={event.pageY} />
    this.inputNode = this.mountComponent(component)
    this.overlayNode?.pop()
    this.overlayNode = null
    document.removeEventListener('click', this.onClick)
  }

  public openInNewTab(url: any) {
    const win = window.open(url, '_blank')
    if (win != null) {
      win.focus()
    }
  }

  public sendProblem = async (data: any) => {
    this.overlayNode?.pop()
    this.inputNode?.pop()
    this.inputNode = null

    // blue dot
    const dot = (
      <div
        style={{width: 24, height: 24, left: data.position.x - 25, top: data.position.y - 25, zIndex: 999999999999999}}
        className="absolute bg-itx-primary-4 border border-white rounded-full"
      />
    )
    const node = this.mountComponent(dot)

    await new Promise((fulfill) => setTimeout(fulfill, 1000))

    const screenshot = await this.requestor.exec('background', 'captureCurrentTab', {format: 'jpeg'})

    // remove dot
    node.pop()

    // open in a new tab after screenshot and dot removing
    this.openInNewTab(`${config.appUrl}/projects/${data.form.projectId}`)

    await APIService.createProblem({
      description: data.form.description,
      image: {
        content: screenshot,
        contentType: 'image/jpg',
      },
      projectId: data.form.projectId,
      title: data.form.title,
    })
  }

  public unmount = () => {
    document.removeEventListener('click', this.onClick)
  }
}

export const chromeContentServiceSelector = selector(identitySelector<string>(), () => new ChromeContentService())
